import { applyI18n } from '../../i18n';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import type { SedrDirectoryTreeNode } from './sedrEvidenceExplorer';

export async function showSedrMoveDialog(input: {
  tree: SedrDirectoryTreeNode;
  currentParentId: string | null;
  excludeId?: string;
}): Promise<string | null | undefined> {
  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: 'files.owner.moveTo',
      helpTopic: dialogHelpTopic('sedrMove'),
      className: 'files-move-dialog',
    });

    shell.body.innerHTML = `
      <p class="muted" data-i18n="files.owner.moveToHint"></p>
      <div class="files-move-dialog__tree" id="sedr-move-dialog-tree"></div>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'sedr-move-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'button primary';
    confirmButton.id = 'sedr-move-confirm';
    confirmButton.innerHTML = '<span data-i18n="common.confirm"></span>';

    shell.actions.append(cancelButton, confirmButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    let selectedParentId: string | null = input.currentParentId;
    const treeHost = shell.body.querySelector('#sedr-move-dialog-tree') as HTMLElement;

    const finish = (value: string | null | undefined) => {
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(undefined),
    });

    const isExcluded = (node: SedrDirectoryTreeNode): boolean => {
      if (!input.excludeId || node.id === null) return false;
      return node.id === input.excludeId;
    };

    const renderTree = () => {
      treeHost.replaceChildren();

      const rootButton = document.createElement('button');
      rootButton.type = 'button';
      rootButton.className = `files-move-dialog__node${selectedParentId === null ? ' files-move-dialog__node--active' : ''}`;
      rootButton.textContent = '/';
      rootButton.addEventListener('click', () => {
        selectedParentId = null;
        renderTree();
      });
      treeHost.append(rootButton);

      const list = document.createElement('ul');
      list.className = 'files-move-dialog__list';
      for (const child of input.tree.children) {
        appendNode(list, child, 0);
      }
      treeHost.append(list);
    };

    const appendNode = (list: HTMLElement, node: SedrDirectoryTreeNode, depth: number) => {
      if (isExcluded(node)) return;

      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `files-move-dialog__node${selectedParentId === node.id ? ' files-move-dialog__node--active' : ''}`;
      button.style.paddingLeft = `${0.75 + depth * 0.85}rem`;
      button.textContent = node.name;
      button.addEventListener('click', () => {
        selectedParentId = node.id;
        renderTree();
      });
      item.append(button);
      list.append(item);

      if (node.children.length > 0) {
        const childList = document.createElement('ul');
        childList.className = 'files-move-dialog__list';
        for (const child of node.children) {
          appendNode(childList, child, depth + 1);
        }
        item.append(childList);
      }
    };

    cancelButton.addEventListener('click', () => finish(undefined));
    confirmButton.addEventListener('click', () => finish(selectedParentId));
    renderTree();
  });
}
